import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlassCard, CardHeader, Button, Icon, KpiCard, StatusPill, DataTable } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useAllVendors } from '../data/useAllVendors';
import { useVendorState, useRegistryStatus, useLatestLinks } from '../data/useVendorState';
import { useAuth } from '../../../../contexts/AuthContext';
import { selfRegularizationService } from '../../../../services/apiService';
import { RUN_STATUS_PILL, formatInr, checkByKey, RUN_CHECK_FILTERS, filterRunsByCheck, runCountsByCheck, toneFor } from '../data/selfRegularization';
import { baseHealth, suggestRuns } from '../data/regularizationInsights';
import { BaseHealthStrip, SuggestionRow } from '../components/RegularizationInsights';
import { WhoNeedsAsking } from '../components/WhoNeedsAsking';
import { formatShortDate } from '../data/format';
import { RegularizationModeTabs } from '../components/RegularizationModeTabs';
import { RegistryNotice, CheckChip } from '../components/SelfRegularizationBits';

/**
 * Self-regularization — the third way to regularize a vendor. Nothing is sent to anyone: what is
 * already on record is verified against the registries and an admin approves the result into the
 * record. This screen is the front door: what it is, what has been run, and the button to run more.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');
const LIVE = new Set(['running', 'queued']);

const HOW = [
  { icon: 'checklist', title: 'Choose checks', text: 'GST, PAN, bank account, MSME — each priced per vendor.' },
  { icon: 'groups', title: 'Pick vendors', text: 'The cost multiplies as you tick, before anything runs.' },
  { icon: 'fact_check', title: 'We verify', text: 'Against the registries. No email goes to any vendor.' },
  { icon: 'task_alt', title: 'You approve', text: 'Registry values fill the empty and stale fields.' },
];

/**
 * One pill per check above the runs list, so the runs of one activity can be read on their own —
 * while a run is still going as much as after. "All" is the default; the choice lives in the URL
 * (?check=bank), so coming back from a run keeps it.
 */
function CheckFilterPills({ value, counts, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Show runs by check">
      {RUN_CHECK_FILTERS.map((f) => {
        const selected = f.key === value;
        const tone = f.key === 'all' ? null : toneFor(f.key);
        const count = counts[f.key] ?? 0;
        const look = selected
          ? tone ? `${tone.solid} border-transparent` : 'bg-cos-ink text-white border-transparent'
          : 'border-cos-line bg-white/70 text-cos-ink hover:bg-cos-surface-hover';
        return (
          <button
            type="button"
            key={f.key}
            aria-pressed={selected}
            onClick={() => onChange(f.key)}
            title={f.key === 'all' ? 'Every run' : `Runs that made the ${f.label} check`}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${look}`}
          >
            <Icon name={f.icon} className={`text-[14px] ${selected ? '' : tone ? tone.text : 'text-cos-ink-secondary'}`} />
            {f.label}
            <span className={`mono rounded-full px-1.5 text-2xs ${selected ? 'bg-white/25' : 'bg-cos-surface-hover text-cos-ink-muted'}`}>{num(count)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SelfRegularizationHub() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const runs = useApiData(() => selfRegularizationService.listRuns(), [], { cacheKey: 'selfreg:runs' });
  const rows = useMemo(() => runs.data?.data || [], [runs.data]);
  const [howOpen, setHowOpen] = useState(false);

  // Which check the runs list is showing. Kept in the URL so a run page can be opened and closed without losing it.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('check');
  const checkFilter = RUN_CHECK_FILTERS.some((f) => f.key === requested) ? requested : 'all';
  // Plain form on purpose: this router (6.0) has no functional updater for setSearchParams.
  const setCheckFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'all') next.delete('check');
    else next.set('check', key);
    setSearchParams(next, { replace: true });
  };
  const visibleRows = useMemo(() => filterRunsByCheck(rows, checkFilter), [rows, checkFilter]);
  const runCountsByPill = useMemo(() => runCountsByCheck(rows), [rows]);
  const filterLabel = RUN_CHECK_FILTERS.find((f) => f.key === checkFilter)?.label || 'All';

  // The whole base, for the picture at the top and the suggestions: what to run, on whom.
  const all = useAllVendors();
  const base = all.vendors || [];
  const registry = useRegistryStatus();
  // An approved run changes what counts as verified; the count of approved runs is the trigger.
  const approvedCount = rows.filter((r) => r.approved_at).length;
  const vendorState = useVendorState({ deps: [approvedCount] });
  const ready = base.length > 0 && !vendorState.loading;
  // "Who needs asking" — the same panel as the through-the-vendor hub. Asking means mail, which is an
  // admin's to send, so only an admin loads the links it is worked out from.
  const links = useLatestLinks({ enabled: isAdmin });
  const priceOf = useMemo(() => {
    const byKey = new Map((registry.checks || []).map((c) => [c.key, c.price_inr]));
    return (key) => byKey.get(key) ?? checkByKey(key)?.price_inr;
  }, [registry.checks]);
  const health = useMemo(() => (ready ? baseHealth(base, vendorState.stateOf) : null), [ready, base, vendorState.stateOf]);
  const runSuggestions = useMemo(() => (ready ? suggestRuns(base, vendorState.stateOf, priceOf) : []), [ready, base, vendorState.stateOf, priceOf]);
  const wizard = (s, auto) => `/campaigning-tool/regularization/self/new?checks=${s.checks.join(',')}&need=unverified:${s.checks[0]}${auto ? '&auto=1' : ''}`;
  // A number on the strip is a filter: "not yet" opens the wizard on those; "missing" is the vendor's to supply — the other mode.
  const pickHealth = (kind, key) => {
    if (kind === 'missing') {
      if (isAdmin) navigate(`/campaigning-tool/regularization/send?need=missing:${key}&ask=${key}`);
      return;
    }
    navigate(`/campaigning-tool/regularization/self/new?checks=${key}&need=unverified:${key}`);
  };

  // A run in progress keeps its row moving without anyone pressing refresh; so do answers arriving.
  const anyLive = rows.some((r) => LIVE.has(r.status) || (r.counts?.ack_requested || 0) > 0);
  useEffect(() => {
    if (!anyLive) return undefined;
    const t = setInterval(runs.refetch, 2500);
    return () => clearInterval(t);
  }, [anyLive, runs.refetch]);

  usePageChrome(
    () => ({
      title: 'Vendor Regularization',
      subtitle: 'Verify what is on record against the registries — no email to the vendor',
      actions: isAdmin ? (
        <Button variant="primary" icon="play_arrow" onClick={() => navigate('/campaigning-tool/regularization/self/new')}>
          New verification run
        </Button>
      ) : (
        <Button variant="secondary" icon="lock" disabled title="Only an admin can start a run — verification is paid for per call">
          Admin starts runs
        </Button>
      ),
    }),
    [isAdmin, navigate]
  );

  const kpis = useMemo(() => {
    const verified = rows.reduce((n, r) => n + (r.counts?.verified || 0), 0);
    const attention = rows.reduce((n, r) => n + (r.counts?.attention || 0), 0);
    const awaiting = rows.filter((r) => r.status === 'awaiting_approval').length;
    const spent = rows.reduce((n, r) => n + Number(r.cost_actual_inr || 0), 0);
    return { verified, attention, awaiting, spent };
  }, [rows]);

  const columns = [
    {
      key: 'name',
      header: 'Run',
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-cos-ink">{r.name}</div>
          <div className="mono text-2xs text-cos-ink-muted">
            #{r.id} · {r.created_by?.full_name || 'Admin'}
          </div>
        </div>
      ),
    },
    {
      key: 'checks',
      header: 'Checks',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.checks || []).map((k) => (
            <CheckChip key={k} checkKey={k} size="sm" />
          ))}
        </div>
      ),
    },
    { key: 'vendor_count', header: 'Vendors', align: 'right', render: (r) => <span className="mono text-cos-ink">{num(r.vendor_count)}</span> },
    {
      key: 'result',
      header: 'Result',
      render: (r) => {
        const c = r.counts || {};
        if (LIVE.has(r.status)) {
          return (
            <span className="text-[12px] text-cos-ink-secondary">
              <span className="mono font-semibold text-cos-ink">{num(c.done)}</span> of {num(c.total)} checked
            </span>
          );
        }
        return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
            <span className="text-cos-ink-secondary">
              <span className="mono font-semibold text-cos-teal-deep">{num(c.verified)}</span> verified
            </span>
            {c.attention > 0 && (
              <span className="text-cos-ink-secondary">
                <span className="mono font-semibold text-cos-status-coral">{num(c.attention)}</span> need attention
              </span>
            )}
            {c.skipped > 0 && (
              <span className="text-cos-ink-muted">
                <span className="mono font-semibold">{num(c.skipped)}</span> skipped
              </span>
            )}
            {c.acknowledged > 0 && (
              <span className="text-cos-ink-secondary">
                <span className="mono font-semibold text-cos-teal-deep">{num(c.acknowledged)}</span> acknowledged
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      render: (r) => (
        <div className="text-right">
          <div className="mono font-semibold text-cos-ink">{formatInr(r.cost_actual_inr)}</div>
          {LIVE.has(r.status) && <div className="mono text-2xs text-cos-ink-muted">of {formatInr(r.cost_estimate_inr)}</div>}
        </div>
      ),
    },
    { key: 'started', header: 'Started', render: (r) => <span className="text-cos-ink-secondary">{formatShortDate(r.started_at || r.created_at)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const p = RUN_STATUS_PILL[r.status] || { pill: 'draft', label: r.status };
        return <StatusPill status={p.pill} label={p.label} />;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Coordinators only have this mode; the other one sends mail, which is an admin's to do. */}
        {isAdmin ? <RegularizationModeTabs mode="self" /> : <span />}
        <button type="button" onClick={() => setHowOpen((o) => !o)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-cos-teal-deep hover:underline">
          <Icon name="help" className="text-[15px]" /> How this works
        </button>
      </div>

      {howOpen && (
        <GlassCard accentRail>
          <p className="mb-4 text-[13px] leading-[19px] text-cos-ink-secondary">
            <span className="font-semibold text-cos-ink">Amber is moving to SAP, which needs the latest details for every vendor.</span>{' '}
            This checks what is already on record against the registries without asking anyone to re-enter anything.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {HOW.map((h, i) => (
              <div key={h.title} className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-cos-teal-faint text-cos-teal-deep">
                  <Icon name={h.icon} className="text-[20px]" weight={500} />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-bold text-cos-ink">
                    <span className="mono text-2xs text-cos-ink-muted">0{i + 1}</span>
                    {h.title}
                  </div>
                  <p className="mt-0.5 text-[12px] leading-[18px] text-cos-ink-secondary">{h.text}</p>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <RegistryNotice status={registry} />

      <BaseHealthStrip health={health} loading={(all.loading && !all.vendors) || vendorState.loading} onPick={pickHealth} />

      <SuggestionRow
        title="What to run"
        subtitle="Everyone whose input is on record and was never confirmed by the registry. One click takes you to the review with them ticked."
        suggestions={runSuggestions}
        actionLabelFor={(s) => `Run on ${num(s.vendorIds.length)}`}
        onAct={(s) => navigate(wizard(s, true))}
        onPreview={(s) => navigate(wizard(s, false))}
        disabled={!isAdmin || !ready || !!registry.providerProblem}
      />

      {isAdmin && (
        <WhoNeedsAsking base={base} stateOf={vendorState.stateOf} links={links} ready={ready && !links.loading} disabled={false} onReminded={links.refetch} />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon="history" label="Runs" value={num(rows.length)} caption="All time" tone="teal" />
        <KpiCard icon="verified" label="Vendors verified" value={num(kpis.verified)} caption="A registry answered for them" tone="teal" />
        <KpiCard
          icon="pending_actions"
          label="Awaiting approval"
          value={num(kpis.awaiting)}
          caption={kpis.awaiting ? 'Runs with results to approve' : 'Nothing waiting on you'}
          tone={kpis.awaiting ? 'beige' : 'teal'}
        />
        <KpiCard icon="payments" label="Spent" value={formatInr(kpis.spent)} caption="Registry calls, all runs" tone="neutral" />
      </div>

      <GlassCard padded={false}>
        <div className="px-5 pt-5">
          <CardHeader
            title="Verification runs"
            subtitle={
              rows.length
                ? checkFilter === 'all'
                  ? 'Newest first · click a run for its results'
                  : visibleRows.length === 0
                    ? `None of the ${num(rows.length)} runs made this check`
                    : `${num(visibleRows.length)} of ${num(rows.length)} runs, newest first · click a run for its results`
                : 'Every run and what it found will be listed here'
            }
            icon={<Icon name="fact_check" className="text-[20px] text-cos-teal-deep" />}
            className={rows.length ? 'mb-3' : ''}
          />
          {rows.length > 0 && (
            <div className="pb-4">
              <CheckFilterPills value={checkFilter} counts={runCountsByPill} onChange={setCheckFilter} />
            </div>
          )}
        </div>
        {runs.loading && !runs.data ? (
          <p className="px-5 py-10 text-center text-sm text-cos-ink-secondary">Loading runs…</p>
        ) : runs.error && !runs.data ? (
          <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
            <Icon name="cloud_off" className="text-[32px] text-cos-status-coral" />
            <p className="text-sm text-cos-status-coral">{runs.error}</p>
            <Button variant="secondary" icon="refresh" onClick={runs.refetch}>Try again</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-5 py-14 text-center">
            <Icon name="fact_check" className="text-[34px] text-cos-ink-secondary/50" />
            <p className="max-w-[440px] text-sm text-cos-ink-secondary">
              No verification runs yet. {isAdmin ? 'Start one to check what is on record against the registries — nothing is sent to any vendor.' : 'An admin starts them; the results will appear here for you to read.'}
            </p>
            {isAdmin && (
              <Button variant="primary" icon="play_arrow" onClick={() => navigate('/campaigning-tool/regularization/self/new')}>
                New verification run
              </Button>
            )}
          </div>
        ) : (
          visibleRows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
            <Icon name="filter_alt_off" className="text-[32px] text-cos-ink-secondary/50" />
            <p className="text-sm text-cos-ink-secondary">No run has made the {filterLabel} check yet.</p>
            <Button variant="secondary" icon="apps" onClick={() => setCheckFilter('all')}>
              Show all runs
            </Button>
          </div>
        ) : (
          <DataTable columns={columns} rows={visibleRows} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/campaigning-tool/regularization/self/runs/${r.id}`)} />
        )
        )}
      </GlassCard>
    </div>
  );
}

export default SelfRegularizationHub;
